import { busy, h, mount, toast } from './dom.ts';
import { type Api, createApi, installApi } from './api.ts';
import { loadStore, type Store } from './state.ts';
import { previewView } from './views/preview.ts';
import { peopleView } from './views/people.ts';
import { brandsView } from './views/brands.ts';
import { EDITOR_ROUTE, layoutEditorView } from './views/layout-editor.ts';
import { campaignsView } from './views/campaigns.ts';
import { deployView } from './views/deploy.ts';
import { statsView } from './views/stats.ts';
import { installView } from './views/install.ts';

const TABS = [
  ['apercu', 'Aperçu', previewView],
  ['campagnes', 'Campagnes', campaignsView],
  ['statistiques', 'Statistiques', statsView],
  ['personnes', 'Personnes', peopleView],
  ['chartes', 'Chartes', brandsView],
  ['deploiement', 'Déploiement', deployView],
] as const;

const app = document.getElementById('app')!;

/**
 * Une page à routage par ancre ne se recharge jamais d'elle-même : un onglet ouvert depuis la veille
 * fait tourner l'ancien code alors que le back-office a été republié (vu le 22/09/2026). On compare
 * donc la version embarquée à celle en ligne — à l'ouverture, à chaque changement d'onglet, au retour
 * sur la page et toutes les cinq minutes — et on propose de recharger.
 */
function watchVersion(): void {
  const mine = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  if (!mine) return;
  let shown = false;
  const check = async () => {
    if (shown) return;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = await res.json();
      if (version && version !== mine) {
        shown = true;
        document.body.append(
          h(
            'div',
            { class: 'update-banner', role: 'status' },
            `Nouvelle version du back-office en ligne (${version}). Cet onglet fait tourner ${mine}.`,
            h('button', { onclick: () => location.reload() }, 'Recharger'),
          ),
        );
      }
    } catch { /* hors ligne : on réessaiera */ }
  };
  check();
  window.addEventListener('hashchange', check);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check());
  setInterval(check, 5 * 60 * 1000);
}

/** Version de build (meta injectée par scripts/build-web.ts) : affichée à l'écran pour qu'on nomme ce qu'on regarde. */
const APP_VERSION = document.querySelector('meta[name="app-version"]')?.getAttribute('content') ?? 'dev';
const versionTag = () => h('span', { class: 'muted version', title: 'Version du back-office (date UTC et commit)' }, `v ${APP_VERSION}`);

function loginView(api: Api): HTMLElement {
  const email = h('input', { id: 'login-email', type: 'email', placeholder: 'prenom.n@bonjourbonsoir.paris' });
  const password = h('input', { id: 'login-password', type: 'password', placeholder: 'Mot de passe' });
  const enter = h('button', null, 'Se connecter');
  const submit = () =>
    busy(enter, async () => {
      await api.signInWithPassword(email.value.trim(), password.value);
      location.reload();
    });
  enter.addEventListener('click', submit);
  password.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') submit();
  });

  return h(
    'main',
    { class: 'login panel' },
    h('h1', null, 'Signatures'),
    h('p', { class: 'muted' }, 'Accès réservé aux administrateurs.'),
    versionTag(),
    email,
    password,
    enter,
    h('p', { class: 'muted sep' }, 'ou'),
    h('button', { class: 'ghost', onclick: () => api.signInWithGoogle() }, 'Se connecter avec Google'),
    h('button', {
      class: 'ghost',
      onclick: async () => {
        try {
          await api.signInWithEmail(email.value.trim());
          toast('Lien de connexion envoyé par mail');
        } catch (e) {
          toast((e as Error).message, 'error');
        }
      },
    }, 'Recevoir un lien par mail'),
  );
}

async function shell(api: Api, user: string): Promise<void> {
  let store: Store;
  try {
    store = await loadStore(api, user);
  } catch (e) {
    mount(
      app,
      h(
        'main',
        { class: 'login panel' },
        h('h1', null, 'Accès refusé'),
        h('p', null, `${user} : ${(e as Error).message}`),
        h(
          'p',
          { class: 'muted' },
          'Votre adresse doit figurer dans la table « admins ».',
        ),
        h('button', {
          onclick: async () => {
            await api.signOut();
            location.reload();
          },
        }, 'Se déconnecter'),
      ),
    );
    return;
  }
  const content = h('div', { class: 'content' });
  const nav = h('nav', { class: 'tabs' });
  const render = () => {
    if (content.parentElement && !content.isConnected) return; // page d'installation affichée
    const hash = location.hash.replace('#', '') || 'apercu';
    // Éditeur de mise en page : plein écran, sur sa propre route (#chartes/<id>/mise-en-page)
    const editor = EDITOR_ROUTE.exec(hash);
    document.body.classList.toggle('editor-full', !!editor);
    content.classList.toggle('full', !!editor);
    if (editor) {
      mount(content, layoutEditorView(store, editor[1]));
      return;
    }
    const current = hash.split('/')[0];
    const tab = TABS.find(([id]) => id === current) ?? TABS[0];
    mount(
      nav,
      TABS.map(([id, label]) => h('a', { href: `#${id}`, class: id === tab[0] ? 'active' : '' }, label)),
    );
    mount(content, tab[2](store, render));
  };
  window.addEventListener('hashchange', render);
  mount(
    app,
    h(
      'header',
      { class: 'top' },
      h(
        'div',
        { class: 'brand' },
        h('strong', null, 'BONJOUR/BONSOIR'),
        h('span', null, 'Signatures'),
        api.demo ? h('span', { class: 'chip demo' }, 'DÉMO') : null,
      ),
      nav,
      h(
        'div',
        { class: 'user' },
        h('span', { class: 'muted' }, user),
        versionTag(),
        api.demo ? null : h('button', {
          class: 'ghost',
          onclick: async () => {
            await api.signOut();
            location.reload();
          },
        }, 'Déconnexion'),
      ),
    ),
    content,
  );
  render();
}

const installToken = (): string | null =>
  location.pathname.match(/^\/install\/([A-Za-z0-9-]+)\/?$/)?.[1] ??
    location.hash.match(/^#\/install\/([A-Za-z0-9-]+)$/)?.[1] ?? null;

async function boot(): Promise<void> {
  watchVersion();
  // La page d'installation n'attend ni supabase-js (CDN) ni une session : elle s'affiche avec un
  // simple fetch, et tout de suite (état de chargement), même si le CDN ou la session traînent.
  if (installToken()) {
    const view = installView(installApi(), installToken()!);
    mount(app, h('main', { class: 'install' }, h('p', { class: 'muted' }, 'Chargement…')));
    return mount(app, await view);
  }
  const api = await createApi();
  let current: string | null = null;
  // Une seule route change de page : la page d'installation. Les onglets vivent dans shell().
  const route = async () => {
    const token = installToken();
    const key = token ? `install:${token}` : 'app';
    if (key === current) return;
    current = key;
    if (token) return mount(app, await installView(api, token));
    const user = await api.currentUser();
    if (!user) return mount(app, loginView(api));
    await shell(api, user);
  };
  globalThis.addEventListener('hashchange', route);
  await route();
}

boot().catch((e) => mount(app, h('pre', { class: 'panel' }, String(e?.stack ?? e))));
